// Parsing a coach's schedule CSV into events the import endpoint accepts.
//
// This is where the guessing lives. The server takes one strict shape —
// YYYY-MM-DD dates, HH:MM times, white|dark cap colors — and refuses anything
// else. A coach's spreadsheet contains none of that: dates are "10/4/2026" or
// "Oct 4", the cap column says "Home", and the header row might say "Opp".
//
// Doing that translation in the browser is deliberate: it's the only place that
// can show the coach what it decided before anything is written.

export interface ParsedRow {
  /** 1-based, matching what a spreadsheet shows and what the API reports. */
  rowNumber: number;
  eventDate: string;
  startTime: string;
  opponent: string;
  location: string;
  capColor: "white" | "dark";
  levelLabel: string;
  notes: string;
  /** Set when the row can't be imported; it's shown and the row is skipped. */
  error?: string;
}

export type FieldName =
  | "eventDate"
  | "startTime"
  | "opponent"
  | "location"
  | "capColor"
  | "levelLabel"
  | "notes";

/**
 * Header spellings we'll map without being asked, lowercased.
 *
 * Deliberately not clever: a wrong auto-guess is worse than no guess, because
 * the coach has to notice it rather than make a choice. Anything unrecognised
 * is left unmapped for them to set.
 */
const HEADER_ALIASES: Record<FieldName, string[]> = {
  eventDate: ["date", "game date", "day", "event date"],
  startTime: ["time", "start", "start time", "game time"],
  opponent: ["opponent", "opp", "vs", "versus", "against", "team"],
  location: ["location", "pool", "venue", "site", "where"],
  capColor: ["cap", "caps", "cap color", "cap colour", "home/away", "home away", "h/a"],
  levelLabel: ["level", "age group", "division", "squad"],
  notes: ["notes", "note", "comment", "comments"],
};

/** Split CSV text into rows of cells, honouring quoted fields and CRLF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        // "" inside a quoted field is a literal quote.
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else if (ch !== "\r") {
      cell += ch;
    }
  }
  // Whatever's left is the last row, unless the file ended with a newline.
  if (cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

/** Guess which column holds which field, by header name. */
export function guessMapping(headers: string[]): Partial<Record<FieldName, number>> {
  const out: Partial<Record<FieldName, number>> = {};
  const normalized = headers.map((h) => h.trim().toLowerCase());
  for (const [field, aliases] of Object.entries(HEADER_ALIASES) as [FieldName, string[]][]) {
    const idx = normalized.findIndex((h) => aliases.includes(h));
    if (idx >= 0) out[field] = idx;
  }
  return out;
}

/**
 * Turn a coach's date into YYYY-MM-DD.
 *
 * Handles ISO, M/D/YYYY, M/D/YY, and "Oct 4" / "4 Oct" with an optional year.
 * A two-digit year maps to 2000+, and a missing year takes `fallbackYear` —
 * spreadsheets routinely omit it because the season is obvious to the person
 * who typed it.
 *
 * Ambiguity is resolved as US month-first, matching every club in the system.
 * Returns null rather than guessing further; the row then shows an error the
 * coach can fix rather than silently landing on the wrong day.
 */
export function normalizeDate(input: string, fallbackYear: number): string | null {
  const s = input.trim();
  if (!s) return null;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (iso) return build(+iso[1], +iso[2], +iso[3]);

  const slash = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/.exec(s);
  if (slash) {
    const year = slash[3] ? (slash[3].length === 2 ? 2000 + +slash[3] : +slash[3]) : fallbackYear;
    return build(year, +slash[1], +slash[2]);
  }

  const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
  const words = s.toLowerCase().replace(/,/g, " ").split(/\s+/).filter(Boolean);
  let month: number | null = null;
  let day: number | null = null;
  let year: number | null = null;
  for (const w of words) {
    const mi = MONTHS.findIndex((m) => w.startsWith(m));
    if (mi >= 0 && month === null) {
      month = mi + 1;
      continue;
    }
    const n = Number(w.replace(/(st|nd|rd|th)$/, ""));
    if (!Number.isFinite(n)) continue;
    if (n >= 1000) year = n;
    else if (day === null) day = n;
  }
  if (month !== null && day !== null) return build(year ?? fallbackYear, month, day);

  return null;
}

/** Reject dates that don't exist (2026-02-31) rather than let MySQL do it. */
function build(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}`;
}

/**
 * Turn a coach's time into HH:MM, or "" for a blank.
 *
 * Blank is not an error — "time TBD" is a normal row. Anything present but
 * unreadable IS an error, because silently dropping a time a coach typed is
 * how someone arrives an hour late.
 */
export function normalizeTime(input: string): { value: string } | { error: string } {
  const s = input.trim();
  if (!s) return { value: "" };

  const m = /^(\d{1,2})(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i.exec(s);
  if (!m) return { error: `couldn't read the time "${s}"` };

  let hour = +m[1];
  const minute = m[2] ? +m[2] : 0;
  const meridiem = m[3]?.toLowerCase().replace(/\./g, "");

  if (meridiem === "pm" && hour < 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;
  if (hour > 23 || minute > 59) return { error: `"${s}" isn't a real time` };

  const pad = (n: number) => String(n).padStart(2, "0");
  return { value: `${pad(hour)}:${pad(minute)}` };
}

/**
 * Read a cap color. Accepts the white/dark vocabulary and the home/away one,
 * because plenty of spreadsheets say "Home" — and home is the white caps.
 */
export function normalizeCapColor(input: string): "white" | "dark" {
  const s = input.trim().toLowerCase();
  if (["dark", "d", "away", "a", "blue", "black"].includes(s)) return "dark";
  return "white";
}

/**
 * Apply a mapping to the data rows and normalize every cell.
 *
 * `fallbackYear` fills in for dates that omit one. Rows carrying an `error` are
 * shown to the coach and excluded from the import rather than dropped silently.
 */
export function buildRows(
  rows: string[][],
  mapping: Partial<Record<FieldName, number>>,
  fallbackYear: number
): ParsedRow[] {
  const cell = (row: string[], field: FieldName) => {
    const idx = mapping[field];
    return idx === undefined ? "" : (row[idx] ?? "").trim();
  };

  return rows.map((row, i) => {
    const rowNumber = i + 1;
    const rawDate = cell(row, "eventDate");
    const date = normalizeDate(rawDate, fallbackYear);
    const time = normalizeTime(cell(row, "startTime"));

    const base: ParsedRow = {
      rowNumber,
      eventDate: date ?? "",
      startTime: "value" in time ? time.value : "",
      opponent: cell(row, "opponent"),
      location: cell(row, "location"),
      capColor: normalizeCapColor(cell(row, "capColor")),
      levelLabel: cell(row, "levelLabel"),
      notes: cell(row, "notes"),
    };

    if (!rawDate) return { ...base, error: "no date" };
    if (!date) return { ...base, error: `couldn't read the date "${rawDate}"` };
    if ("error" in time) return { ...base, error: time.error };
    return base;
  });
}
